import React from "react";

import { FormattedTimestampText } from "@/shared/components/RobotoMarkdown/FormattedTimestampText";

/**
 * Renders an AI-emitted bare timestamp mention (originally written as
 * `t=<n><unit>` in prose — optionally wrapped in an inline-code highlight)
 * as a non-clickable inline span, formatted in the user's chosen
 * TimestampFormat.
 *
 * Inserted by the remarkTimestampMentions plugin via the `ai-ts:<ns>` (plain)
 * or `ai-ts-hl:<ns>` (highlighted) URL scheme. Distinct from {@link Timestamp}
 * (which renders an `<a>` with seek behavior for explicit
 * `[label](timestamp:<ns>)` links) — bare mentions are reformatted text
 * only, never clickable.
 */
export const InlineTimestamp: React.FC<{
  ns: bigint;
  highlighted?: boolean;
}> = ({ ns, highlighted = false }) => {
  const content = <FormattedTimestampText ns={ns} />;
  // `title` surfaces the raw ns to assistive tech and on hover, so users
  // can recognize the formatted text as an actionable timestamp.
  const title = `${ns.toString()} ns`;
  if (highlighted) {
    return (
      <code data-roboto-inline-ts={ns.toString()} title={title}>
        {content}
      </code>
    );
  }
  return (
    <span data-roboto-inline-ts={ns.toString()} title={title}>
      {content}
    </span>
  );
};
